import React, { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { IndianRupee, Save, X, UserPlus, Trash2, ShieldAlert, Loader2 } from "lucide-react";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Pill, Button, Input, TextArea } from "../../components/common/Primitives";
import { Modal, ModalFooter } from "../../components/common/Modal";
import api, { formatApiError } from "../../lib/api";

const inr = (v) => "₹" + Number(v || 0).toLocaleString("en-IN");

export default function AdminEsignPricing() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [globalPrice, setGlobalPrice] = useState(25);
  const [savingGlobal, setSavingGlobal] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ email: "", price_inr: 15, note: "" });

  const load = () => {
    setLoading(true);
    api.get("/admin/esign-pricing")
      .then((r) => { setData(r.data); setGlobalPrice(r.data.global_price_inr); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const saveGlobal = async () => {
    setSavingGlobal(true);
    try {
      await api.patch("/admin/esign-pricing", { price_inr: Number(globalPrice) });
      toast.success(`Global fee updated to ${inr(globalPrice)}/doc.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingGlobal(false); }
  };

  const removeOverride = async (user_id, email) => {
    if (!window.confirm(`Remove the per-doc override for ${email}?`)) return;
    try {
      await api.post(`/admin/users/${user_id}/esign-price`, { price_inr: null });
      toast.success(`Override cleared for ${email}.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    try {
      // Resolve user_id from email
      const { data: search } = await api.get(`/admin/users/search`, { params: { q: addForm.email } });
      const target = (search.users || search.items || search || []).find(
        (u) => (u.email || "").toLowerCase() === addForm.email.toLowerCase()
      );
      if (!target?.user_id) throw new Error(`No user found for ${addForm.email}`);
      const priceRaw = addForm.price_inr === "" ? null : Number(addForm.price_inr);
      const body = { price_inr: priceRaw, note: addForm.note || "" };
      await api.post(`/admin/users/${target.user_id}/esign-price`, body);
      toast.success(`Override set for ${addForm.email}.`);
      setAddOpen(false);
      setAddForm({ email: "", price_inr: 15, note: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <DashboardShell title="E-Sign Pricing" subtitle="ADMIN · FEES + WAIVERS">
      <Toaster richColors position="top-right" />

      {loading || !data ? (
        <Card><div className="flex items-center gap-2 text-ink-muted text-sm"><Loader2 size={14} className="animate-spin" /> Loading…</div></Card>
      ) : (
        <div className="space-y-6">
          {/* Global fee */}
          <Card testId="esign-global-price-card">
            <div className="overline mb-2">Global per-document fee</div>
            <div className="text-sm text-ink-muted mb-4">
              Charged to every founder wallet on <code>envelope.send</code>. Valid range:
              <strong> {inr(data.min_price_inr)} – {inr(data.max_price_inr)}</strong>.
            </div>
            <div className="flex items-end gap-3 flex-wrap">
              <label>
                <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1">Price (₹)</div>
                <div className="flex items-center gap-2 border border-navy/15 rounded-md px-3 py-2 bg-white focus-within:border-gold">
                  <IndianRupee size={14} className="text-ink-muted" />
                  <input
                    type="number"
                    min={data.min_price_inr}
                    max={data.max_price_inr}
                    value={globalPrice}
                    onChange={(e) => setGlobalPrice(e.target.value)}
                    data-testid="esign-global-price-input"
                    className="bg-transparent outline-none w-24 font-mono text-sm"
                  />
                </div>
              </label>
              <Button
                variant="gold"
                onClick={saveGlobal}
                disabled={savingGlobal}
                data-testid="esign-global-price-save"
              >
                {savingGlobal ? <Loader2 size={14} className="inline animate-spin mr-2" /> : <Save size={14} className="inline mr-2" />}
                Save
              </Button>
              <div className="flex-1" />
              <div className="text-xs font-mono text-ink-muted">
                Currently <span className="text-ink font-heading text-lg">{inr(data.global_price_inr)}</span>/doc
              </div>
            </div>
          </Card>

          {/* Per-user overrides */}
          <Card testId="esign-overrides-card">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="overline mb-1">Per-user overrides</div>
                <div className="text-xs text-ink-muted">
                  {data.overrides.length} active override{data.overrides.length === 1 ? "" : "s"}. Set 0 to fully waive the fee (audit note required).
                </div>
              </div>
              <Button variant="gold" onClick={() => setAddOpen(true)} data-testid="esign-override-add-btn">
                <UserPlus size={14} className="inline mr-2" /> Add override
              </Button>
            </div>

            {data.overrides.length === 0 ? (
              <div className="text-sm text-ink-muted italic py-4">
                No per-user overrides. Every founder currently pays the global fee.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] font-mono uppercase text-ink-muted tracking-wider border-b border-navy/[0.08]">
                      <th className="text-left py-2 pr-3">User</th>
                      <th className="text-left py-2 pr-3">Role</th>
                      <th className="text-left py-2 pr-3">Price</th>
                      <th className="text-left py-2 pr-3">Note</th>
                      <th className="text-left py-2 pr-3">Set by</th>
                      <th className="text-left py-2 pr-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.overrides.map((o) => (
                      <tr key={o.user_id} className="border-t border-navy/[0.06]" data-testid={`esign-override-${o.user_id}`}>
                        <td className="py-2.5 pr-3">
                          <div className="font-heading text-ink">{o.name || o.email}</div>
                          <div className="text-[10px] font-mono text-ink-muted">{o.email}</div>
                        </td>
                        <td className="py-2.5 pr-3 text-xs text-ink capitalize">{o.role}</td>
                        <td className="py-2.5 pr-3">
                          {o.esign_price_inr === 0 ? (
                            <span className="inline-flex items-center gap-1 text-emerald-800 font-heading">
                              <ShieldAlert size={12} /> Waived
                            </span>
                          ) : (
                            <span className="font-heading text-ink">{inr(o.esign_price_inr)}</span>
                          )}
                        </td>
                        <td className="py-2.5 pr-3 text-xs text-ink-muted italic max-w-[240px] truncate" title={o.esign_price_note}>
                          {o.esign_price_note || "—"}
                        </td>
                        <td className="py-2.5 pr-3 text-[11px] font-mono text-ink-muted">
                          {o.esign_price_updated_by || "—"}
                          <br />
                          <span className="text-ink-muted/70">{(o.esign_price_updated_at || "").slice(0, 10)}</span>
                        </td>
                        <td className="py-2.5 pr-3">
                          <button
                            onClick={() => removeOverride(o.user_id, o.email)}
                            className="text-red-600 hover:text-red-800 text-xs flex items-center gap-1"
                            data-testid={`esign-override-remove-${o.user_id}`}
                          >
                            <Trash2 size={12} /> Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Add per-user override" testId="esign-override-add-modal">
          <form onSubmit={submitAdd} className="space-y-3">
            <Input label="User email"
                    value={addForm.email}
                    onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                    placeholder="founder@example.com"
                    required
                    testId="esign-override-email" />
            <Input label={`Price (₹) — ${data?.min_price_inr}–${data?.max_price_inr}, or 0 to waive`}
                    type="number"
                    min={0}
                    max={data?.max_price_inr}
                    value={addForm.price_inr}
                    onChange={(e) => setAddForm({ ...addForm, price_inr: e.target.value })}
                    testId="esign-override-price" />
            <TextArea label="Audit note (required if price = 0)"
                       value={addForm.note}
                       onChange={(e) => setAddForm({ ...addForm, note: e.target.value })}
                       rows={2}
                       testId="esign-override-note" />
            <ModalFooter>
              <Button variant="ghost" type="button" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button type="submit" variant="gold" testId="esign-override-submit">
                <Save size={14} className="inline mr-2" /> Save override
              </Button>
            </ModalFooter>
          </form>
        </Modal>
    </DashboardShell>
  );
}
